import React, { useState, useRef } from "react";
import { Modal, ModalBody, Table, Badge, Button, Spinner } from "reactstrap";
import GeneralButton from "../button/GeneralButton";
import toast from "react-hot-toast";
import { uploadGroupExcelFile } from "../../firebase/utility";
import { importGroupsFromExcel, importVariantsToGroupFromExcel } from "../../api/products.api";
import { IoCloseSharp } from "react-icons/io5";
import { 
  FiUploadCloud, 
  FiFileText, 
  FiLayers, 
  FiGrid, 
  FiInfo,
  FiCheckCircle,
  FiAlertTriangle,
  FiXCircle,
  FiArrowLeft,
  FiImage
} from "react-icons/fi";
import Separator from "../Separator";

const GROUP_CREATION_COLUMNS = [
  "group_name",
  "mfg_slug",
  "sub_category_slug",
  "description",
  "image_url",
  "long_description",
  "variant_attribute_keys"
];

const VARIANT_ADDITION_COLUMNS = [
  "group_slug",
  "product_slug",
  "sort_order",
  "variant_attributes"
];

const parseVariantAttributesPreview = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try {
        return JSON.parse(trimmed);
      } catch (e) {
        // Fallback to key-value string parsing
      }
    }
    const result = {};
    const parts = trimmed.split(/[,;\n]/);
    parts.forEach((part) => {
      const idx = part.indexOf(":");
      if (idx !== -1) {
        const k = part.substring(0, idx).trim();
        const v = part.substring(idx + 1).trim();
        if (k) result[k] = v;
      } else if (part.trim()) {
        result[part.trim()] = "";
      }
    });
    return result;
  }
  return {};
};

const parseAttributeKeysPreview = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    return raw.split(/[,;|]/).map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

const extractErrorRowNumber = (row, idx) => {
  if (row?.row !== undefined && row?.row !== null) return row.row;
  if (row?.rowNumber !== undefined && row?.rowNumber !== null) return row.rowNumber;
  if (row?.row_number !== undefined && row?.row_number !== null) return row.row_number;
  if (row?.line !== undefined && row?.line !== null) return row.line;
  if (row?.rowIndex !== undefined && row?.rowIndex !== null) return row.rowIndex;
  return idx + 1;
};

const extractErrorMessage = (row) => {
  if (row?.reason) return typeof row.reason === "string" ? row.reason : JSON.stringify(row.reason);
  if (row?.error) return typeof row.error === "string" ? row.error : (row.error?.message || JSON.stringify(row.error));
  if (row?.message) return typeof row.message === "string" ? row.message : JSON.stringify(row.message);
  if (row?.errorMessage) return row.errorMessage;
  if (row?.details) return typeof row.details === "string" ? row.details : JSON.stringify(row.details);
  if (Array.isArray(row?.errors) && row.errors.length > 0) return row.errors.join(", ");
  return "Validation error";
};

const extractErrorIdentifier = (row) => {
  if (row?.identifier) return row.identifier;
  if (row?.group_slug) return row.group_slug;
  if (row?.product_slug) return row.product_slug;
  if (row?.group_name) return row.group_name;
  if (row?.slug) return row.slug;
  if (row?.sku) return row.sku;
  if (row?.name) return row.name;
  const msg = row?.reason || row?.error || row?.message || "";
  if (typeof msg === "string") {
    const match = msg.match(/["']([^"']+)["']/);
    if (match && match[1]) {
      return match[1];
    }
  }
  return "—";
};

const BulkUploadGroupsModal = ({ isOpen, toggle, refreshData }) => {
  const [activeTab, setActiveTab] = useState("groups"); // 'groups' | 'variants'
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState({});
  const [uploadedFilePath, setUploadedFilePath] = useState("");
  const [manualPath, setManualPath] = useState("");
  const [useManualPath, setUseManualPath] = useState(false);
  const [activePreviewTab, setActivePreviewTab] = useState("valid"); // 'valid' | 'invalid'
  const fileInputRef = useRef(null);

  const resetState = () => {
    setFile(null);
    setLoading(false);
    setImporting(false);
    setIsPreviewMode(false);
    setPreviewData({});
    setUploadedFilePath("");
    setManualPath("");
    setUseManualPath(false);
    setActivePreviewTab("valid");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleToggle = () => {
    resetState();
    toggle();
  };

  const handleTabChange = (tab) => {
    resetState();
    setActiveTab(tab);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  // STEP 1: Upload to Storage & Request Preview (preview: true)
  const handleUploadAndPreview = async () => {
    let targetPath = manualPath.trim();

    if (!useManualPath && !file) {
      toast.error("Please select an Excel file first.");
      return;
    }

    setLoading(true);
    try {
      if (!useManualPath) {
        const subfolder = activeTab === "groups" ? "excel-sheets-groups" : "excel-sheets-group-variants";
        const { path } = await uploadGroupExcelFile(file, subfolder);
        targetPath = path;
      }

      setUploadedFilePath(targetPath);

      if (activeTab === "groups") {
        importGroupsFromExcel(targetPath, true, (res) => {
          setLoading(false);
          if (res?.status === 200 && res?.data) {
            setPreviewData(res.data?.data || res.data || {});
            setIsPreviewMode(true);
            toast.success("Groups spreadsheet preview loaded successfully.");
          } else {
            toast.error(res?.message || "Failed to load groups preview.");
          }
        });
      } else {
        importVariantsToGroupFromExcel(targetPath, true, (res) => {
          setLoading(false);
          if (res?.status === 200 && res?.data) {
            setPreviewData(res.data?.data || res.data || {});
            setIsPreviewMode(true);
            toast.success("Variants spreadsheet preview loaded successfully.");
          } else {
            toast.error(res?.message || "Failed to load variants preview.");
          }
        });
      }
    } catch (error) {
      toast.error("Error uploading file: " + (error?.message || error));
      setLoading(false);
    }
  };

  // STEP 2: Final Confirmation & Import (preview: false)
  const handleFinalImport = () => {
    if (!uploadedFilePath) {
      toast.error("Missing file path for import.");
      return;
    }

    setImporting(true);
    if (activeTab === "groups") {
      importGroupsFromExcel(uploadedFilePath, false, (res) => {
        setImporting(false);
        if (res?.status === 200 || res?.success) {
          toast.success(res?.data?.message || "Groups imported successfully from Excel!");
          if (refreshData) refreshData();
          handleToggle();
        } else {
          toast.error(res?.message || "Failed to import groups from Excel.");
        }
      });
    } else {
      importVariantsToGroupFromExcel(uploadedFilePath, false, (res) => {
        setImporting(false);
        if (res?.status === 200 || res?.success) {
          toast.success(res?.data?.message || "Variants added to groups successfully from Excel!");
          if (refreshData) refreshData();
          handleToggle();
        } else {
          toast.error(res?.message || "Failed to import variants to groups.");
        }
      });
    }
  };

  const isGroupsTab = activeTab === "groups";

  // Data Normalization for Preview Tables
  const rawValid = 
    previewData?.validRows || 
    previewData?.validGroups || 
    previewData?.validVariants || 
    (isGroupsTab ? previewData?.groups : previewData?.variants) || 
    previewData?.data || 
    previewData?.items || 
    previewData?.rows || 
    (Array.isArray(previewData) ? previewData : []);

  const validRows = Array.isArray(rawValid) ? rawValid : [];

  const rawInvalid = 
    previewData?.invalidRows || 
    previewData?.invalidGroups || 
    previewData?.invalidVariants || 
    previewData?.errors || 
    previewData?.failedRows || 
    [];

  const invalidRows = Array.isArray(rawInvalid) ? rawInvalid : [];

  const totalProcessed = 
    previewData?.totalRows ?? 
    previewData?.totalProcessed ?? 
    previewData?.total ?? 
    (validRows.length + invalidRows.length);

  const validCount = 
    previewData?.linked ?? 
    previewData?.validCount ?? 
    previewData?.createdCount ?? 
    previewData?.successCount ?? 
    validRows.length;

  const invalidCount = 
    previewData?.skipped ?? 
    previewData?.invalidCount ?? 
    previewData?.errorCount ?? 
    previewData?.failedCount ?? 
    invalidRows.length;

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} centered size="xl" className="modal-dialog-scrollable">
      <ModalBody className="p-4 bg-white">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center gap-3">
            <div className="bg-primary-soft p-3 rounded-circle text-primary">
              <FiLayers size={24} />
            </div>
            <div>
              <h4 className="primary-color fw-bold mb-0">Bulk Upload from File</h4>
              <p className="text-muted small mb-0 mt-1">
                Preview or import formatted Excel spreadsheets to create groups or add variants in bulk.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-light btn-sm rounded-circle p-1 d-flex align-items-center justify-content-center text-muted"
            onClick={handleToggle}
            style={{ width: "32px", height: "32px" }}
          >
            <IoCloseSharp size={20} />
          </button>
        </div>
        <Separator />

        {/* Tab Navigation (Disabled during Preview Mode) */}
        {!isPreviewMode && (
          <div className="d-flex gap-2 my-3 p-1 bg-light rounded-3 border">
            <button
              type="button"
              className={`btn btn-sm flex-fill fw-bold py-2 rounded-2 transition-all d-flex align-items-center justify-content-center gap-2 ${
                isGroupsTab ? "bg-white text-primary shadow-sm" : "text-muted border-0 bg-transparent"
              }`}
              onClick={() => handleTabChange("groups")}
              disabled={loading}
            >
              <FiLayers size={16} /> 1. Create Groups from Excel
            </button>
            <button
              type="button"
              className={`btn btn-sm flex-fill fw-bold py-2 rounded-2 transition-all d-flex align-items-center justify-content-center gap-2 ${
                !isGroupsTab ? "bg-white text-primary shadow-sm" : "text-muted border-0 bg-transparent"
              }`}
              onClick={() => handleTabChange("variants")}
              disabled={loading}
            >
              <FiGrid size={16} /> 2. Add Variants to Groups
            </button>
          </div>
        )}

        <div className="mt-3">
          {!isPreviewMode ? (
            /* ========================================================================= */
            /* UPLOAD & PREVIEW TRIGGER VIEW */
            /* ========================================================================= */
            <div className="p-4 border rounded-3 text-center bg-light shadow-2xs">
              <div className="bg-primary-soft text-primary d-inline-flex p-3 rounded-circle mb-3">
                <FiUploadCloud size={36} />
              </div>
              <h5 className="fw-bold mb-1">
                {isGroupsTab ? "Upload Groups Creation Spreadsheet" : "Upload Variant Addition Spreadsheet"}
              </h5>
              <p className="text-muted small mb-3">
                Supports <code>.xlsx</code>, <code>.xls</code> or <code>.csv</code> files formatted for {isGroupsTab ? "group creation" : "variant addition"}.
              </p>

              {/* Expected Template Header Keys */}
              <div className="bg-white p-3 rounded border text-start mb-4">
                <div className="d-flex align-items-center gap-1 mb-2">
                  <FiInfo className="text-primary" size={14} />
                  <span className="fw-bold text-dark small">
                    Required Excel Column Headers:
                  </span>
                </div>
                <div className="d-flex flex-wrap gap-1">
                  {(isGroupsTab ? GROUP_CREATION_COLUMNS : VARIANT_ADDITION_COLUMNS).map((col, idx) => (
                    <span key={idx} className="badge bg-light text-dark border px-2 py-1" style={{ fontSize: "11px" }}>
                      {col}
                    </span>
                  ))}
                </div>
              </div>

              {!useManualPath ? (
                <div className="mb-3">
                  <input
                    type="file"
                    className="form-control"
                    accept=".xlsx, .xls, .csv"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    disabled={loading}
                  />
                  {file && (
                    <div className="d-flex align-items-center justify-content-center gap-2 mt-2 text-success small">
                      <FiFileText size={15} />
                      <span className="fw-medium">{file.name}</span> ({(file.size / 1024).toFixed(1)} KB)
                    </div>
                  )}
                </div>
              ) : (
                <div className="mb-3 text-start">
                  <label className="form-label small fw-bold text-muted">Storage File Path</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={isGroupsTab ? "e.g. excel-sheets-groups/groups.xlsx" : "e.g. excel-sheets-group-variants/variants.xlsx"}
                    value={manualPath}
                    onChange={(e) => setManualPath(e.target.value)}
                    disabled={loading}
                  />
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-link btn-sm text-decoration-none p-0 text-secondary"
                  onClick={() => setUseManualPath(!useManualPath)}
                  disabled={loading}
                >
                  {useManualPath ? "← Upload file from computer" : "Use existing Storage Path →"}
                </button>

                <GeneralButton
                  title={
                    loading
                      ? "Uploading & Previewing..."
                      : (isGroupsTab ? "Preview Groups Sheet" : "Preview Variants Sheet")
                  }
                  className="blue-button mb-0"
                  width="240px"
                  loading={loading}
                  disabled={loading || (!useManualPath && !file) || (useManualPath && !manualPath.trim())}
                  handleClick={handleUploadAndPreview}
                />
              </div>
            </div>
          ) : (
            /* ========================================================================= */
            /* SPREADSHEET PREVIEW VIEW (preview: true received) */
            /* ========================================================================= */
            <div>
              {/* Header Stats Bar */}
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 pb-2 border-bottom">
                <div>
                  <h5 className="fw-bold mb-0">
                    {isGroupsTab ? "Group Creation Preview" : "Variant Addition Preview"}
                  </h5>
                  <span className="text-muted small">
                    File: <code className="text-dark">{uploadedFilePath}</code> ({totalProcessed} total rows processed)
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    color="light"
                    size="sm"
                    className="border d-flex align-items-center gap-1"
                    onClick={() => setIsPreviewMode(false)}
                    disabled={importing}
                  >
                    <FiArrowLeft size={14} /> Back to Upload
                  </Button>
                </div>
              </div>

              {/* Status Counters */}
              <div className="d-flex gap-2 mb-3">
                <button
                  type="button"
                  className={`btn btn-sm px-3 py-1.5 d-flex align-items-center gap-2 rounded-2 ${
                    activePreviewTab === "valid" ? "btn-success text-white" : "btn-outline-success"
                  }`}
                  onClick={() => setActivePreviewTab("valid")}
                >
                  <FiCheckCircle size={14} />
                  <span>
                    Valid {isGroupsTab ? "Groups" : "Variants"} ({validCount})
                  </span>
                </button>
                {invalidCount > 0 && (
                  <button
                    type="button"
                    className={`btn btn-sm px-3 py-1.5 d-flex align-items-center gap-2 rounded-2 ${
                      activePreviewTab === "invalid" ? "btn-danger text-white" : "btn-outline-danger"
                    }`}
                    onClick={() => setActivePreviewTab("invalid")}
                  >
                    <FiXCircle size={14} />
                    <span>Invalid / Errors ({invalidCount})</span>
                  </button>
                )}
              </div>

              {/* TAB 1 PREVIEW: Valid Groups */}
              {activePreviewTab === "valid" && isGroupsTab && (
                <div style={{ maxHeight: "460px", overflow: "auto", border: "1px solid #e2e8f0", borderRadius: "6px" }}>
                  <Table striped hover responsive size="sm" className="align-middle mb-0" style={{ fontSize: "12.5px" }}>
                    <thead style={{ position: "sticky", top: 0, background: "#f8fafc", zIndex: 2 }}>
                      <tr className="text-secondary small">
                        <th style={{ width: "50px" }}>Row</th>
                        <th style={{ width: "60px" }}>Image</th>
                        <th style={{ width: "22%" }}>Group Name</th>
                        <th style={{ width: "18%" }}>Manufacturer / SubCategory</th>
                        <th style={{ width: "25%" }}>Description</th>
                        <th style={{ width: "25%" }}>Variant Attribute Keys</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validRows.length > 0 ? (
                        validRows.map((row, idx) => {
                          const name = row.group_name || row.name || row.title || row.groupName || "—";
                          const mfg = row.mfg_slug || row.mfg || row.manufacturer || "—";
                          const subCat = row.sub_category_slug || row.sub_category || row.subCategory || "—";
                          const desc = row.description || row.short_description || "";
                          const image = row.image_url || row.imageUrl || row.image || "";
                          const rawAttrKeys = row.variant_attribute_keys || row.variantAttributes || row.attributeKeys || row.attributes || [];
                          const attrKeys = parseAttributeKeysPreview(rawAttrKeys);

                          return (
                            <tr key={idx}>
                              <td className="text-muted small">{row.rowNumber || idx + 1}</td>
                              <td>
                                {image ? (
                                  <img
                                    src={image}
                                    alt={name}
                                    style={{ width: "36px", height: "36px", objectFit: "contain", borderRadius: "4px", border: "1px solid #dee2e6" }}
                                    onError={(e) => { e.target.style.display = "none"; }}
                                  />
                                ) : (
                                  <div className="bg-light text-muted d-flex align-items-center justify-content-center rounded" style={{ width: "36px", height: "36px" }}>
                                    <FiImage size={16} />
                                  </div>
                                )}
                              </td>
                              <td>
                                <div className="fw-bold text-dark">{name}</div>
                              </td>
                              <td>
                                <div className="small"><strong>Mfg:</strong> <span className="font-monospace">{mfg}</span></div>
                                <div className="small text-muted"><strong>SubCat:</strong> <span className="font-monospace">{subCat}</span></div>
                              </td>
                              <td>
                                <div className="small text-muted text-truncate" style={{ maxWidth: "220px" }} title={desc}>
                                  {desc || "—"}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex flex-wrap gap-1">
                                  {attrKeys.length > 0 ? (
                                    attrKeys.map((k, kIdx) => (
                                      <Badge key={kIdx} color="light" className="border text-dark px-2 py-1 font-monospace" style={{ fontSize: "11px" }}>
                                        {k}
                                      </Badge>
                                    ))
                                  ) : (
                                    <span className="text-muted small">None</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center py-4 text-muted">
                            {validCount > 0
                              ? `${validCount} valid group(s) ready to create.`
                              : "No valid groups found in this sheet."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* TAB 2 PREVIEW: Valid Variants */}
              {activePreviewTab === "valid" && !isGroupsTab && (
                <div style={{ maxHeight: "460px", overflow: "auto", border: "1px solid #e2e8f0", borderRadius: "6px" }}>
                  <Table striped hover responsive size="sm" className="align-middle mb-0" style={{ fontSize: "12.5px" }}>
                    <thead style={{ position: "sticky", top: 0, background: "#f8fafc", zIndex: 2 }}>
                      <tr className="text-secondary small">
                        <th style={{ width: "50px" }}>Row</th>
                        <th style={{ width: "25%" }}>Group Slug</th>
                        <th style={{ width: "25%" }}>Product Slug / SKU</th>
                        <th style={{ width: "12%" }}>Sort Order</th>
                        <th style={{ width: "38%" }}>Variant Attributes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validRows.length > 0 ? (
                        validRows.map((row, idx) => {
                          const groupSlug = row.group_slug || row.groupSlug || row.group || "—";
                          const productSlug = row.product_slug || row.productSlug || row.product || row.sku || "—";
                          const sortOrder = row.sort_order ?? row.sortOrder ?? 0;
                          const rawAttrs = row.variant_attributes || row.variantAttributes || row.attributes || {};
                          const attrs = parseVariantAttributesPreview(rawAttrs);

                          return (
                            <tr key={idx}>
                              <td className="text-muted small">{row.rowNumber || row.row || idx + 1}</td>
                              <td>
                                <div className="fw-bold font-monospace text-primary">{groupSlug}</div>
                              </td>
                              <td>
                                <div className="fw-semibold font-monospace text-dark">{productSlug}</div>
                              </td>
                              <td>
                                <Badge color="secondary" pill className="px-2 py-1">
                                  {sortOrder}
                                </Badge>
                              </td>
                              <td>
                                <div className="d-flex flex-wrap gap-1">
                                  {Object.keys(attrs).length > 0 ? (
                                    Object.entries(attrs).map(([k, v], aIdx) => (
                                      <span key={aIdx} className="badge bg-light text-dark border px-2 py-1" style={{ fontSize: "11px" }}>
                                        <strong>{k}:</strong> {String(v)}
                                      </span>
                                    ))
                                  ) : (
                                    <span className="text-muted small">None</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center py-4 text-muted">
                            {validCount > 0
                              ? `${validCount} valid variant(s) ready to import.`
                              : "No valid variants found in this sheet."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* INVALID ROWS PREVIEW TABLE */}
              {activePreviewTab === "invalid" && (
                <div style={{ maxHeight: "440px", overflow: "auto", border: "1px solid #fed7d7", borderRadius: "6px" }}>
                  <Table striped hover responsive size="sm" className="align-middle mb-0" style={{ fontSize: "13px" }}>
                    <thead style={{ position: "sticky", top: 0, background: "#fff5f5", zIndex: 2 }}>
                      <tr className="text-danger small">
                        <th style={{ width: "85px" }}>Row</th>
                        <th style={{ width: "30%" }}>Identifier / Slug</th>
                        <th>Error Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invalidRows.length > 0 ? (
                        invalidRows.map((row, idx) => {
                          const rowNum = extractErrorRowNumber(row, idx);
                          const identifier = extractErrorIdentifier(row);
                          const errorMsg = extractErrorMessage(row);

                          return (
                            <tr key={idx}>
                              <td className="fw-bold text-dark">
                                <span className="badge bg-light text-dark border px-2 py-1">
                                  Row {rowNum}
                                </span>
                              </td>
                              <td className="font-monospace text-dark fw-semibold" style={{ wordBreak: "break-all" }}>
                                {identifier}
                              </td>
                              <td className="text-danger" style={{ wordBreak: "break-word" }}>
                                <FiAlertTriangle size={14} className="me-1 flex-shrink-0" />
                                <span>{errorMsg}</span>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center py-4 text-muted">
                            No error records found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* Bottom Actions */}
              <div className="mt-4 pt-3 border-top d-flex justify-content-between align-items-center flex-wrap gap-2">
                <span className="text-muted small">
                  Ready to import <strong className="text-dark">{validCount}</strong> valid {isGroupsTab ? "groups" : "variants"} into the system.
                </span>
                <GeneralButton
                  title={
                    importing
                      ? (isGroupsTab ? "Importing Groups..." : "Importing Variants...")
                      : (isGroupsTab ? `Upload & Create Groups (${validCount})` : `Upload & Add Variants (${validCount})`)
                  }
                  className="blue-button mb-0"
                  width="260px"
                  loading={importing}
                  disabled={importing || validCount === 0}
                  handleClick={handleFinalImport}
                />
              </div>
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default BulkUploadGroupsModal;

